// react
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link, useNavigate, useParams } from '@tanstack/react-router';

// material ui icons
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';

// material ui
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';

// api
import { dataIntegrationsOpenApi } from '@/api/generated/dataIntegrations/endpoints/dataIntegrations';

// shared components
import DataGridNoRowsCustomOverlay from '@/components/ui/datagrid/DataGridNoRowsOverlay';
import { DataGridToolbar } from '@/components/ui/datagrid/DataGridToolbar';
import RowActions from '@/components/ui/datagrid/RowActions';

// components
import { supplierOptions } from '@/features/eforsyning-integration/eforsyningSupplierOptions';
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';
import { showSnackbar } from '@/redux/snackbarSlice';

const getStatusChipColor = (isActive) => (isActive ? 'success' : 'default');

const normalizeSupplierId = (value) =>
  String(value ?? '')
    .trim()
    .toUpperCase();

const getSupplierOptionValue = (option) =>
  normalizeSupplierId(option?.value ?? option?.id ?? option?.supplierId);

const getSupplierOptionLabel = (option) =>
  String(
    option?.label ??
      option?.name ??
      option?.text ??
      getSupplierOptionValue(option),
  );

const supplierLabelById = Object.fromEntries(
  supplierOptions.map((option) => [
    getSupplierOptionValue(option),
    getSupplierOptionLabel(option),
  ]),
);

const formatLatestTimestamp = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(parsedDate);
};

const EforsyningIntegrationDataGrid = ({
  integrations,
  isLoading,
  onDeleteIntegration,
}) => {
  const { t } = useTranslation([
    'eforsyning-integration',
    'common',
    'units-gateways-sensors',
  ]);
  const navigate = useNavigate();
  const { accountId } = useParams({
    from: '/$accountId/_user/eforsyning-integration/',
  });
  const dispatch = useDispatch();
  const [triggerTestConnection, { isFetching: isTestingConnection }] =
    dataIntegrationsOpenApi.useLazyTestEforsyningConnectionIntegrationsEforsyningTestPartitionKeyRowKeyGetQuery();

  const rows = (integrations ?? []).map((integration) => {
    return {
      ...integration,
      id: `${integration?.PartitionKey ?? ''}-${integration?.RowKey ?? ''}`,
      RowKey: integration?.RowKey ?? '',
      PartitionKey: integration?.PartitionKey ?? '',
      Active: Boolean(integration?.Active),
      MeterType: integration?.MeterType ?? '',
      SupplierId: integration?.SupplierId ?? '',
      ReCalcName: integration?.ReCalcName ?? '',
      ReMoniUnitId: integration?.ReMoniUnitId ?? null,
      LatestDataPointTimestamp: integration?.LatestDataPointTimestamp ?? null,
      Address_Backup: integration?.Address_Backup ?? '',
    };
  });

  const [rowModesModel, setRowModesModel] = useState({});
  const [testingRowId, setTestingRowId] = useState(null);

  const [dataGridState, setDataGridState] = useLocalStorageJson(
    'Eforsyning-Integration-DataGrid-Presets',
    {
      presets: [
        {
          name: t('defaultPresetName', {
            defaultValue: 'Default',
          }),
          visibilityList: {},
          density: 'standard',
        },
      ],
      chosenPreset: 0,
    },
  );

  const handleVisibilityChange = (model) => {
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((p, i) =>
        i === prev.chosenPreset ? { ...p, visibilityList: model } : p,
      ),
    }));
  };

  const handleDensityChange = (density) => {
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((p, i) =>
        i === prev.chosenPreset ? { ...p, density } : p,
      ),
    }));
  };

  const handleTestIntegration = async (row) => {
    if (!row?.PartitionKey || !row?.RowKey) {
      dispatch(
        showSnackbar({
          message: t('testMissingIdentifiers', {
            defaultValue:
              'Unable to test integration because identifiers are missing.',
          }),
          severity: 'error',
        }),
      );
      return;
    }

    setTestingRowId(row.id);

    const result = await triggerTestConnection(
      {
        partitionKey: Number(row.PartitionKey),
        rowKey: row.RowKey,
      },
      false,
    );

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('testSuccess', {
            defaultValue: 'Connection test succeeded.',
          }),
          severity: 'success',
        }),
      );
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
    setTestingRowId(null);
  };

  const handleEditIntegration = (row) => {
    if (!row?.RowKey) {
      return;
    }

    void navigate({
      to: '/$accountId/eforsyning-integration/$integrationId/edit',
      params: {
        accountId,
        integrationId: encodeURIComponent(row.RowKey),
      },
    });
  };

  const columns = [
    {
      field: 'ReCalcName',
      headerName: t('name', {
        ns: 'eforsyning-integration',
        defaultValue: 'Name',
      }),
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {params.value ||
              t('notAvailable', {
                ns: 'eforsyning-integration',
                defaultValue: 'Not Available',
              })}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'ReMoniUnitId',
      headerName: t('reMoniUnitId', {
        defaultValue: 'ReMoniUnitId',
      }),
      width: 170,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value ? (
            <Typography
              variant="body2"
              component={Link}
              to="/$accountId/units/$unitId"
              params={{
                accountId,
                unitId: String(params.value),
              }}
              color="primary"
              sx={{
                textDecoration: 'none',
                ':hover': { textDecoration: 'underline' },
              }}
            >
              {String(params.value)}
            </Typography>
          ) : (
            <Typography variant="body2">
              {t('notAvailable', {
                ns: 'eforsyning-integration',
                defaultValue: 'Not Available',
              })}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'LatestDataPointTimestamp',
      headerName: t('latestDataPointTimestamp', {
        defaultValue: 'LatestDataPointTimestamp',
      }),
      width: 240,
      renderCell: (params) => {
        const formattedTimestamp = formatLatestTimestamp(params.value);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {formattedTimestamp ? (
              <Chip
                label={formattedTimestamp}
                size="small"
                variant="outlined"
              />
            ) : (
              <Typography variant="body2">
                {t('notAvailable', {
                  ns: 'eforsyning-integration',
                  defaultValue: 'Not Available',
                })}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'MeterType',
      headerName: t('common.type', { ns: 'common', defaultValue: 'Type' }),
      width: 160,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {params.value
              ? t(`meterTypes.${params.value}`, {
                  defaultValue: params.value,
                })
              : t('common.notAvailable', {
                  ns: 'common',
                  defaultValue: 'Not Available',
                })}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'SupplierId',
      headerName: t('supplier', {
        defaultValue: 'Supplier',
      }),
      width: 170,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {supplierLabelById[normalizeSupplierId(params.value)] ||
              t('notAvailable', {
                ns: 'eforsyning-integration',
                defaultValue: 'Not Available',
              })}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'Active',
      headerName: t('status', {
        ns: 'eforsyning-integration',
        defaultValue: 'Status',
      }),
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={
              params.value
                ? t('active', {
                    ns: 'eforsyning-integration',
                    defaultValue: 'Active',
                  })
                : t('inactive', {
                    ns: 'eforsyning-integration',
                    defaultValue: 'Inactive',
                  })
            }
            color={getStatusChipColor(params.value)}
            size="small"
            variant="outlined"
          />
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: t('units.actions', {
        ns: 'units-gateways-sensors',
        defaultValue: 'Actions',
      }),
      width: 140,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            gap: 0.5,
          }}
        >
          <Tooltip
            disableInteractive
            title={t('testConnection', {
              defaultValue: 'Test connection',
            })}
            placement="top"
          >
            <span>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  void handleTestIntegration(params.row);
                }}
                disabled={testingRowId === params.row.id && isTestingConnection}
                data-testid="row-action-test-connection"
                sx={{ borderRadius: 2 }}
              >
                {testingRowId === params.row.id && isTestingConnection ? (
                  <CircularProgress size={16} />
                ) : (
                  <PlayCircleOutlineRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <RowActions
            row={params.row}
            onEdit={() => handleEditIntegration(params.row)}
            onDelete={() => onDeleteIntegration?.(params.row)}
            disabled={testingRowId === params.row.id && isTestingConnection}
          />
        </Box>
      ),
    },
  ];

  const localeText = useDatagridLocaleText();

  return (
    <Box data-testid="eforsyning-integration-overview-grid">
      <DataGridPremium
        showToolbar
        rows={rows}
        columns={columns}
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        loading={isLoading}
        noRowsOverlay={DataGridNoRowsCustomOverlay}
        localeText={localeText}
        pagination
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 50 } },
        }}
        pageSizeOptions={[50, 100]}
        density={dataGridState.presets[dataGridState.chosenPreset].density}
        onDensityChange={handleDensityChange}
        onColumnVisibilityModelChange={handleVisibilityChange}
        columnVisibilityModel={{
          ...dataGridState.presets[dataGridState.chosenPreset].visibilityList,
        }}
        slots={{
          toolbar: DataGridToolbar,
        }}
        slotProps={{
          toolbar: {
            showDivider: false,
            showFilters: true,
            showColumns: true,
          },
        }}
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-row': {
            minHeight: '52px !important',
          },
          '& .MuiDataGrid-cell': {
            minHeight: '52px !important',
          },
        }}
      />
    </Box>
  );
};

export default EforsyningIntegrationDataGrid;
